import * as React from "react";
import { inertValue } from "../../../utils/inertValue.js";
import { useIsoLayoutEffect } from "../../../utils/useIsoLayoutEffect.js";
import { useBaseUiId } from "../../internals/useBaseUiId.js";
import { transitionStatusMapping } from "../../internals/stateAttributesMapping.js";
import { useOpenChangeComplete } from "../../internals/useOpenChangeComplete.js";
import { useTransitionStatus } from "../../internals/useTransitionStatus.js";
import { useRenderElement } from "../../internals/useRenderElement.js";
import { useCompositeListItem } from "../../internals/composite/list/useCompositeListItem.js";
import { tabsStateAttributesMapping } from "../root/stateAttributesMapping.js";
import { useTabsRootContext } from "../root/TabsRootContext.js";
import { index } from "./TabsPanelDataAttributes.js";
const stateAttributesMapping = {
  ...tabsStateAttributesMapping,
  ...transitionStatusMapping
};
const TabsPanel = /* @__PURE__ */ React.forwardRef(function TabsPanel2(componentProps, forwardedRef) {
  const {
    className,
    value,
    render,
    keepMounted = false,
    style,
    ...elementProps
  } = componentProps;
  const {
    value: selectedValue,
    getTabIdByPanelValue,
    orientation,
    tabActivationDirection,
    registerMountedTabPanel
  } = useTabsRootContext();
  const id = useBaseUiId();
  const {
    ref: listItemRef,
    index: index$1
  } = useCompositeListItem();
  const open = value === selectedValue;
  const {
    mounted,
    transitionStatus,
    setMounted
  } = useTransitionStatus(open);
  const hidden = !mounted;
  const correspondingTabId = getTabIdByPanelValue(value);
  const state = {
    hidden,
    orientation,
    tabActivationDirection,
    transitionStatus
  };
  const panelRef = React.useRef(null);
  const element = useRenderElement("div", componentProps, {
    state,
    ref: [forwardedRef, listItemRef, panelRef],
    props: [{
      "aria-labelledby": correspondingTabId,
      hidden,
      id,
      role: "tabpanel",
      tabIndex: open ? 0 : -1,
      inert: inertValue(!open),
      // Computed key: a plain literal key fails the DOM-props excess property check.
      [index]: index$1
    }, elementProps],
    stateAttributesMapping
  });
  useOpenChangeComplete({
    open,
    ref: panelRef,
    onComplete() {
      if (!open) {
        setMounted(false);
      }
    }
  });
  useIsoLayoutEffect(() => {
    if (id == null || hidden && !keepMounted) {
      return void 0;
    }
    return registerMountedTabPanel(value, id);
  }, [hidden, keepMounted, value, id, registerMountedTabPanel]);
  const shouldRender = keepMounted || mounted;
  if (!shouldRender) {
    return null;
  }
  return element;
});
if (process.env.NODE_ENV !== "production") TabsPanel.displayName = "TabsPanel";
export {
  TabsPanel
};
//# sourceMappingURL=TabsPanel.js.map
